import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import { Button, Conditional, Else, Heading, If, mappedArray, Row } from "@hex-insights/core";
import {
	UserFilterForm,
	UserFilterFormState,
	UserFilterFormUtils,
	UserFormat,
	UserIndexQuery,
	UserOrderField,
	UserOrderForm,
	UserOrderFormState,
	UserOrderFormValues,
	UserPaginationForm,
	useUserIndexQuery,
} from "@hex-insights/roadium.shared";
import { InternalLink, useActivePageRegistration } from "@hex-insights/router";
import { Table } from "@hex-insights/tables";
import { Main } from "../../../../Components";
import { userHubCreatePageInfo } from "../CreatePage/pageinfo";
import { userHubDetailAuthQRCodePageInfo } from "../DetailPage/AuthQRCode/pageinfo";
import { userHubDetailPageInfo } from "../DetailPage/pageinfo";
import { userHubIndexPageInfo } from "./pageinfo";

const columns: (keyof UserIndexQuery["userConnection"]["edges"][0]["node"] | "authQRCode")[] = [
	"email",
	"accountType",
	"status",
	"authQRCode",
];

function orderFieldToColumn(field: UserOrderField | null) {
	if (field === null) {
		return "id";
	}
	return UserOrderFormValues.orderFieldToModelField[field];
}

function columnToOrderField(column: string) {
	if (column === "id") {
		return null;
	}
	return UserOrderFormValues.modelFieldToOrderField[column];
}

export function UserHubIndexPage() {
	useActivePageRegistration(userHubIndexPageInfo);

	const filterFormState = UserFilterFormState.useFilterFormStateWithQueryStateSync();
	const orderFormState = UserOrderFormState.useOrderFormStateWithQueryStateSync();
	const paginationFormState = IndexForms.usePaginationFormStateWithQueryStateAndLocalStorageSync();

	const filterInputs = IndexForms.useFilterInput(filterFormState.formValues, UserFilterFormUtils.toFilterInputs);
	const orderInput = IndexForms.useOrderInput(orderFormState.formValues);
	const queryVariables = IndexForms.useQueryVariables(
		filterInputs,
		orderInput,
		paginationFormState.formValues,
		paginationFormState.formSetFunctions.cursor,
	);
	const { loading, data, error } = useUserIndexQuery({ variables: queryVariables });

	const clearFilters = React.useCallback(() => {
		UserFilterFormUtils.clearFilters(filterFormState);
	}, [filterFormState]);

	const numActiveFilters = UserFilterFormUtils.countActiveFilters(filterFormState.formValues);
	const [paginationInfo, setPaginationInfo] = IndexForms.usePaginationInfo();

	const tableSortingProps = IndexForms.useTableSortingFromOrderForm(
		orderFormState,
		orderFieldToColumn,
		columnToOrderField,
	);

	const numFillerRows = Math.max(
		(paginationFormState.formValues.resultsPerPage ?? 0) - (data?.userConnection.edges.length ?? 0),
		0,
	);

	return (
		<Main>
			<Row justify="space-between" align="center" style={{ margin: "var(--heading--level-1---margin)" }}>
				<Heading level={1} noMargin>
					Users
				</Heading>

				<InternalLink to={userHubCreatePageInfo.to}>New User</InternalLink>
			</Row>

			<Row
				justify="spaced-start"
				align="center"
				horizontalSpacing="0.25rem"
				withVerticalSpacing
				overflow="wrap"
				style={{ marginBottom: "0.75rem" }}
			>
				<UserFilterForm.Search formState={filterFormState} />
				<UserFilterForm.FilterController formState={filterFormState} numActiveFilters={numActiveFilters} />
				<If condition={numActiveFilters > 0}>
					<Button variant="link" size="small" onClick={clearFilters}>
						Clear
					</Button>
				</If>
				<UserOrderForm.OrderController formState={orderFormState} />
			</Row>

			<Table.Container className="hub__index-page__table__container">
				<Table
					columns={columns as any}
					minColumnWidth="10rem"
					scrollSyncID={paginationFormState.formValues.cursor}
					{...tableSortingProps}
					className="hub__index-page__table"
				>
					<Table.Header className="hub__index-page__table__header">
						<Table.Row>
							<Table.Heading column="email">Email</Table.Heading>
							<Table.Heading column="accountType">Account Type</Table.Heading>
							<Table.Heading column="status">Status</Table.Heading>
							<Table.Heading column="authQRCode">Auth QR Code</Table.Heading>
						</Table.Row>
					</Table.Header>

					<Table.Body className="hub__index-page__table__body">
						<Conditional>
							<If condition={loading}>
								<Table.Row className="hub__index-page__table__row">
									<Table.Cell column="email" colspan={columns.length}>
										Loading...
									</Table.Cell>
								</Table.Row>
								{mappedArray((paginationFormState.formValues.resultsPerPage ?? 1) - 1, (i) => (
									<Table.FillerRow
										key={i}
										className="hub__index-page__table__row hub__index-page__table__row--filler"
									/>
								))}
							</If>

							<Else>
								{data?.userConnection.edges.map((e) => (
									<Table.Row key={e.node.id} className="hub__index-page__table__row">
										<Table.Cell column="email" oneLine>
											<InternalLink to={userHubDetailPageInfo.to(e.node.id)}>{e.node.email}</InternalLink>
										</Table.Cell>
										<Table.Cell column="accountType" oneLine>
											{UserFormat.Fields.accountType(e.node.accountType)}
										</Table.Cell>
										<Table.Cell column="status" oneLine>
											{UserFormat.Fields.status(e.node.status)}
										</Table.Cell>
										<Table.Cell column="authQRCode" oneLine>
											<InternalLink to={userHubDetailAuthQRCodePageInfo.to(e.node.id)}>QR Code</InternalLink>
										</Table.Cell>
									</Table.Row>
								))}
								{mappedArray(numFillerRows, (i) => (
									<Table.FillerRow
										key={i}
										className="hub__index-page__table__row hub__index-page__table__row--filler"
									/>
								))}
							</Else>
						</Conditional>
					</Table.Body>
				</Table>

				<Table.Toolbar>
					<Row justify="space-between" align="center">
						<UserPaginationForm.PaginationForm
							formState={paginationFormState}
							filters={filterInputs}
							order={orderInput}
							setPaginationInfo={setPaginationInfo}
						/>

						<span>
							<Conditional>
								<If condition={loading}>Loading...</If>
								<If condition={!!error}>Something went wrong.</If>
								<If condition={data?.userConnection.edges.length === 0}>No results found.</If>
								<Else>{IndexForms.getPaginationDescription(paginationInfo)}</Else>
							</Conditional>
						</span>
					</Row>
				</Table.Toolbar>
			</Table.Container>
		</Main>
	);
}
